import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    CircularProgress,
    IconButton,
    ListItemButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import LockIcon from "@mui/icons-material/Lock";
import { format } from "date-fns";
import TaskService from "../../services/Task.service";
import AddTaskDialog from "./AddTaskDialog";
import TaskPreviewDialog from "./TaskPreviewDialog";

// ── helpers ────────────────────────────────────────────────────────────────────

function formatDue(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(dateStr) {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr + "T00:00:00") < today;
}

function carryoverColor(count) {
    if (count >= 4) return "error";
    if (count >= 2) return "warning";
    return "default";
}

// ── Focus/backlog task row ──────────────────────────────────────────────────────

function FocusCard({ task, mode, onOpen, onToggleAction }) {
    const due = formatDue(task.due_date);
    const overdue = !task.is_complete && isOverdue(task.due_date);
    const borderColor = task.is_public ? "primary.main" : "success.main";
    const linked = task.project_detail ?? task.quote_detail ?? null;
    const projectNum = linked ? [linked.number, linked.name].filter(Boolean).join(" - ") : null;

    return (
        <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.75 }}>
            <ListItemButton
                onClick={() => onOpen(task)}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1.5,
                    py: 1,
                    border: 1,
                    borderColor: "divider",
                    borderLeft: 3,
                    borderLeftColor: borderColor,
                    borderRadius: 1,
                    alignItems: "flex-start",
                    "&:hover": { bgcolor: "action.hover" },
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        {!task.is_public && (
                            <LockIcon sx={{ fontSize: "0.75rem", color: "text.disabled", flexShrink: 0 }} />
                        )}
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 600,
                                textDecoration: task.is_complete ? "line-through" : "none",
                                color: task.is_complete ? "text.disabled" : "text.primary",
                            }}
                        >
                            {task.title}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                        {projectNum && (
                            <Typography
                                variant="caption"
                                noWrap
                                title={projectNum}
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, color: "secondary.main", maxWidth: "100%" }}
                            >
                                {projectNum}
                            </Typography>
                        )}
                        {due && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    color: overdue ? "error.main" : "text.secondary",
                                    fontWeight: overdue ? 700 : 400,
                                }}
                            >
                                {due}
                            </Typography>
                        )}
                        {mode === "focus" && task.carryover_count > 0 && (
                            <Tooltip title={`Carried over ${task.carryover_count} day${task.carryover_count > 1 ? "s" : ""}`}>
                                <Chip
                                    icon={<EventRepeatIcon sx={{ fontSize: "11px !important" }} />}
                                    label={`${task.carryover_count}d`}
                                    size="small"
                                    color={carryoverColor(task.carryover_count)}
                                    sx={{ height: 16, fontSize: 10, "& .MuiChip-label": { px: 0.5 } }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            </ListItemButton>

            <Tooltip title={mode === "focus" ? "Remove from Today" : "Add to Today"}>
                <IconButton
                    onClick={() => onToggleAction(task)}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 1, alignSelf: "stretch" }}
                    color={mode === "focus" ? "default" : "primary"}
                >
                    {mode === "focus" ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// ── Today Focus tab ───────────────────────────────────────────────────────────

export default function TodayFocusTab({ currentUser, taskLists }) {
    const [selectedListId, setSelectedListId] = useState("");
    const [focusTasks, setFocusTasks] = useState([]);
    const [backlogTasks, setBacklogTasks] = useState([]);
    const [backlogTotal, setBacklogTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [previewTask, setPreviewTask] = useState(null);

    const loadFocusData = useCallback(() => {
        setLoading(true);
        const focusParams = {
            assignee: currentUser.id,
            in_today_focus: "true",
            include_completed: "false",
            page_size: 200,
        };
        const backlogParams = {
            assignee: currentUser.id,
            in_today_focus: "false",
            page_size: 50,
        };
        if (selectedListId) backlogParams.tasklist = selectedListId;

        Promise.all([TaskService.list(focusParams), TaskService.list(backlogParams)])
            .then(([focusRes, backlogRes]) => {
                setFocusTasks(focusRes.data.results ?? focusRes.data ?? []);
                setBacklogTasks(backlogRes.data.results ?? backlogRes.data ?? []);
                setBacklogTotal(backlogRes.data.count ?? (backlogRes.data.results ?? backlogRes.data ?? []).length);
            })
            .catch(() => {
                setFocusTasks([]);
                setBacklogTasks([]);
                setBacklogTotal(0);
            })
            .finally(() => setLoading(false));
    }, [currentUser.id, selectedListId]);

    useEffect(() => {
        loadFocusData();
    }, [loadFocusData]);

    // Auto-carryover stale focus tasks, once per day per user.
    useEffect(() => {
        const key = `focus_refresh_${currentUser.id}`;
        const today = format(new Date(), "yyyy-MM-dd");
        if (localStorage.getItem(key) === today) return;
        TaskService.refreshFocus()
            .then(({ data }) => {
                localStorage.setItem(key, today);
                if (data.carried_over > 0) loadFocusData();
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.id]);

    const addToFocus = (task) => {
        const today = format(new Date(), "yyyy-MM-dd");
        setBacklogTasks((prev) => prev.filter((t) => t.id !== task.id));
        setBacklogTotal((prev) => Math.max(0, prev - 1));
        setFocusTasks((prev) => [...prev, { ...task, in_today_focus: true, focus_date: today }]);
        TaskService.update(task.id, { in_today_focus: true, focus_date: today }).catch(() => {
            setFocusTasks((prev) => prev.filter((t) => t.id !== task.id));
            setBacklogTasks((prev) => [task, ...prev]);
            setBacklogTotal((prev) => prev + 1);
        });
    };

    const removeFromFocus = (task) => {
        setFocusTasks((prev) => prev.filter((t) => t.id !== task.id));
        setBacklogTasks((prev) => [{ ...task, in_today_focus: false, focus_date: null, carryover_count: 0 }, ...prev]);
        setBacklogTotal((prev) => prev + 1);
        TaskService.update(task.id, { in_today_focus: false, focus_date: null, carryover_count: 0 }).catch(() => {
            setFocusTasks((prev) => [...prev, task]);
            setBacklogTasks((prev) => prev.filter((t) => t.id !== task.id));
            setBacklogTotal((prev) => Math.max(0, prev - 1));
        });
    };

    const handleTaskUpdated = (updated) => {
        setPreviewTask(updated);
        const inFocus = focusTasks.some((t) => t.id === updated.id);
        if (inFocus) {
            setFocusTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        } else {
            setBacklogTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        }
    };

    const handleSaved = (saved) => {
        if (editTask) {
            setFocusTasks((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
            setBacklogTasks((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
        } else {
            setBacklogTasks((prev) => [saved, ...prev]);
            setBacklogTotal((prev) => prev + 1);
        }
    };

    const handleDeleted = (deleted) => {
        const wasInFocus = focusTasks.some((t) => t.id === deleted.id);
        setFocusTasks((prev) => prev.filter((t) => t.id !== deleted.id));
        setBacklogTasks((prev) => prev.filter((t) => t.id !== deleted.id));
        if (!wasInFocus) setBacklogTotal((prev) => Math.max(0, prev - 1));
    };

    const carryoverFocus = focusTasks.filter((t) => t.carryover_count > 0);
    const freshFocus = focusTasks.filter((t) => t.carryover_count === 0);

    return (
        <Box>
            {/* Controls */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel shrink>Task List</InputLabel>
                    <Select
                        label="Task List"
                        displayEmpty
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}
                    >
                        <MenuItem value=""><em>All Lists</em></MenuItem>
                        {taskLists.map((tl) => (
                            <MenuItem key={tl.id} value={String(tl.id)}>{tl.title}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
                >
                    Add
                </Button>
            </Stack>

            {/* Today Focus section */}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    Today Focus
                </Typography>
                <Chip label={focusTasks.length} size="small" color="primary" sx={{ fontWeight: 700 }} />
                {carryoverFocus.length > 0 && (
                    <Tooltip title="Carried over from previous days">
                        <Chip
                            icon={<EventRepeatIcon sx={{ fontSize: "12px !important" }} />}
                            label={carryoverFocus.length}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                        />
                    </Tooltip>
                )}
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : focusTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3, fontFamily: "Montserrat, sans-serif" }}>
                    Nothing planned yet — add tasks from your backlog below
                </Typography>
            ) : (
                <Stack spacing={1}>
                    {[...carryoverFocus, ...freshFocus].map((task) => (
                        <FocusCard key={task.id} task={task} mode="focus" onOpen={setPreviewTask} onToggleAction={removeFromFocus} />
                    ))}
                </Stack>
            )}

            {/* Backlog section */}
            <Accordion
                disableGutters
                defaultExpanded
                sx={{ mt: 2.5, boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                            Backlog
                        </Typography>
                        <Chip label={backlogTotal} size="small" color="secondary" sx={{ fontWeight: 700 }} />
                        {backlogTotal > backlogTasks.length && (
                            <Typography variant="caption" color="text.secondary">
                                (showing {backlogTasks.length})
                            </Typography>
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={22} />
                        </Box>
                    ) : backlogTasks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2, fontFamily: "Montserrat, sans-serif" }}>
                            All tasks are in Today Focus
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {backlogTasks.map((task) => (
                                <FocusCard key={task.id} task={task} mode="backlog" onOpen={setPreviewTask} onToggleAction={addToFocus} />
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>

            <TaskPreviewDialog
                task={previewTask}
                onClose={() => setPreviewTask(null)}
                currentUserId={currentUser.id}
                onTaskUpdated={handleTaskUpdated}
                onEdit={(task) => {
                    setPreviewTask(null);
                    setEditTask(task);
                    setDialogOpen(true);
                }}
                onDeleted={handleDeleted}
            />

            <AddTaskDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditTask(null); }}
                onSaved={handleSaved}
                currentUser={currentUser}
                defaultProject={null}
                taskLists={taskLists}
                item={editTask}
            />
        </Box>
    );
}
